import { useState, useRef, useEffect } from 'react';
import { Bell, Wifi, WifiOff, Sun, Moon, ChevronRight, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { NAV_ROUTES, SEVERITY_COLORS, CATEGORY_LABELS } from '../../utils/constants';
import { useTheme } from '../../context/ThemeContext';
import { useAlerts } from '../../hooks/useAlerts';
import { formatRelative } from '../../utils/formatters';

interface TopBarProps {
  title: string;
  criticalCount?: number;
  isOnline?: boolean;
}

// issue #153 / FE-24 historia 1: el contrato (GET /me, claims del token,
// /subunits) no trae ningún nombre de organización por tenant — se escaló
// como hallazgo y quedó bloqueada hasta esta decisión (dueño, 2026-09-17):
// mostrar la marca fija "Paitron Radar", no el nombre del cliente actual.
// Por eso ya no depende de sesión/tenant ni cambia entre logins.
const ORGANIZATION_NAME = 'Paitron Radar';

export function TopBar({ title, criticalCount = 0, isOnline = true }: TopBarProps) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // FE-04: antes leía MOCK_ALERTS directo, desincronizado del criticalCount
  // real que ya llegaba por props (AppShell -> useAlerts()). Misma fuente
  // para el badge y el desplegable.
  const { activeAlerts: allActiveAlerts } = useAlerts();
  const activeAlerts = allActiveAlerts.slice(0, 5);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="topbar" style={{ position: 'relative' }}>
      <h1 className="topbar__title">{title}</h1>
      <div className="topbar__actions" ref={dropdownRef}>
        {/* Organización (#153 / FE-24 historia 1) */}
        <span className="topbar__org" title="Organización">{ORGANIZATION_NAME}</span>

        {/* Theme toggle */}
        <button
          id="topbar-theme-btn"
          className="topbar__bell"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Online/Offline indicator */}
        <div className={clsx('topbar__conn', isOnline ? 'topbar__conn--online' : 'topbar__conn--offline')}
          title={isOnline ? 'Conectado' : 'Sin conexión'}>
          {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
          <span className="topbar__conn-label">{isOnline ? 'En línea' : 'Offline'}</span>
        </div>

        {/* Alerts bell with Dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            id="topbar-alerts-btn"
            className="topbar__bell"
            onClick={() => setShowDropdown(prev => !prev)}
            aria-label={`${criticalCount} alertas críticas activas`}
          >
            <Bell size={20} />
            {criticalCount > 0 && (
              <span className="topbar__bell-badge">{criticalCount > 99 ? '99+' : criticalCount}</span>
            )}
          </button>

          {/* Notifications Dropdown Menu */}
          {showDropdown && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '360px',
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-xl)', boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
              zIndex: 1500, overflow: 'hidden', animation: 'fadeIn 0.15s ease-out'
            }}>
              {/* Header */}
              <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-raised)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Bell size={16} style={{ color: 'var(--accent)' }} />
                  <span style={{ fontWeight: 800, fontSize: '13.5px', color: 'var(--text-primary)' }}>Alertas Recientes</span>
                </div>
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '99px', background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>
                  {activeAlerts.length} activas
                </span>
              </div>

              {/* List */}
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {activeAlerts.length === 0 ? (
                  <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                    <CheckCircle2 size={24} style={{ color: '#10B981', marginBottom: '0.5rem' }} />
                    <p style={{ margin: 0 }}>No hay alertas críticas pendientes</p>
                  </div>
                ) : (
                  activeAlerts.map(alert => {
                    const color = SEVERITY_COLORS[alert.severity];
                    return (
                      <div
                        key={alert.id}
                        onClick={() => {
                          setShowDropdown(false);
                          navigate(NAV_ROUTES.ALERTS);
                        }}
                        style={{
                          padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)',
                          cursor: 'pointer', display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, marginTop: '6px', flexShrink: 0, boxShadow: `0 0 6px ${color}` }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)' }}>
                            {CATEGORY_LABELS[alert.category]} ({alert.vehiclePlate})
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
                            Conductor: {alert.driverName || 'N/A'} • {formatRelative(alert.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate(NAV_ROUTES.ALERTS);
                }}
                style={{
                  width: '100%', padding: '0.75rem', border: 'none', background: 'var(--bg-raised)',
                  borderTop: '1px solid var(--border)', color: 'var(--accent)', fontSize: '12.5px',
                  fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem'
                }}
              >
                Ver todas las alertas <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
